import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { getSlumSoccerFundraisers } from "../graphql/queries";
import { generateClient } from "aws-amplify/api";
import { Loader2 } from "lucide-react";
import { parseFundraiser } from "../utils/fundraiser";
import { FundraiserHeader } from "../components/fundraiser/FundraiserHeader";
import { FundraiserDetails } from "../components/fundraiser/FundraiserDetails";
import { FundraiserProgress } from "../components/fundraiser/FundraiserProgress";
import { FundraiserContent } from "../components/fundraiser/FundraiserContent";
import { FundraiserTeam } from "../components/fundraiser/FundraiserTeam";
import { DonateButton } from "../components/fundraiser/DonateButton";

const client = generateClient();

export function FundraiserPage(props) {
  const { slug } = useParams();
  const [fundraiser, setFundraiser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    fetchData();
    window.scrollTo(0, 0);
  }, [slug]);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      setNotFound(false);
      const response = await client.graphql({
        query: getSlumSoccerFundraisers,
        variables: { fundraiserId: slug },
      });

      const data = response.data.getSlumSoccerFundraisers;
      if (!data) {
        setNotFound(true);
      } else {
        setFundraiser(parseFundraiser(data));
      }
    } catch (error) {
      console.error("Error fetching fundraiser:", error);
      setNotFound(true);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50">
        <Loader2 className="animate-spin text-white" size={64} />
      </div>
    );
  }

  if (notFound || !fundraiser) {
    return (
      <section className="py-24 text-center">
        <h2 className="text-3xl font-bold text-orange-500">
          Fundraiser not found
        </h2>
        <p className="text-gray-600 mt-4">
          The fundraiser you are looking for does not exist.
        </p>
      </section>
    );
  }

  return (
    <>
      <FundraiserHeader
        title={fundraiser.title}
        description={fundraiser.description}
        heroImage={fundraiser.heroImage}
        shareUrl={`${window.location.origin}/fundraiser/${fundraiser.slug}`}
      />

      {/* Donation progress */}
      <section className="relative z-20 -mt-10">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto bg-white rounded-xl shadow-lg border border-orange-100 p-6 md:p-8">
            <FundraiserProgress
              goal={fundraiser.goal}
              raised={fundraiser.raised}
              size="lg"
            />
            <div className="mt-12 flex flex-col md:flex-row justify-between items-center gap-6 md:px-20">
              <div className="flex flex-col">
              <p className="text-gray-800 font-bold text-2xl mb-1 text-center md:text-left">
                Support the Journey. Shape the Future.
              </p>
              <p className="text-gray-600 text-lg text-center md:text-left">
                Every contribution brings the team closer to the tournament.
              </p>
              </div>
              <DonateButton
                label="Donate Now"
                className="px-10 py-3 text-lg"
                fundraiserId={fundraiser.slug}
                fundraiserTitle={fundraiser.title}
                paymentFor="TEAM"
              />
            </div>
          </div>
        </div>
      </section>

      <FundraiserDetails details={fundraiser.tournamentDetails} />

      <FundraiserContent html={fundraiser.content} />

      <FundraiserTeam
        participants={fundraiser.participants}
        fundraiserSlug={fundraiser.slug}
        fundraiserTitle={fundraiser.title}
      />
    </>
  );
}
